import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { InputTextarea } from 'primereact/inputtextarea';
import { ModernButton } from '../../components/ui/ModernButton';
import { FileUpload } from 'primereact/fileupload';
import { Avatar } from 'primereact/avatar';
import { Divider } from 'primereact/divider';
import { Password } from 'primereact/password';
import { MultiSelect } from 'primereact/multiselect';
import { Chip } from 'primereact/chip';
import { Toast } from 'primereact/toast';
import { AgentRoleIndicator } from '../../components/ui/ModernIndicators';

import { ProgressBar } from 'primereact/progressbar';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { useAuth } from '../../contexts/AuthContext';
import { AgentService } from '../../services/agentService';
import { CustomerService } from '../../services/customerService';
import {
	ProfileImageService,
	MAX_PROFILE_IMAGE_BYTES,
	ALLOWED_PROFILE_IMAGE_TYPES,
	formatFileSize
} from '../../services/profileImageService';
import { InsightService } from '../../services/insightService';
import { Agent } from '../../types/agent';
import { auth } from '../../config/firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { getInitials } from '../../utils/stringHelpers';
import './ProfilePage.css';

interface ProfileFormData {
	firstName: string;
	lastName: string;
	displayName: string;
	email: string;
	phone: string;
	companyName: string;
	territory: string;
	licenseNumber: string;
	specialties: string[];
	bio: string;
	profileImage?: string;
}

interface PasswordFormData {
	currentPassword: string;
	newPassword: string;
	confirmPassword: string;
}

// `bio` is persisted on the agent document but is not declared on the shared Agent type,
// so widen it locally rather than reading an undeclared property.
interface AgentProfile extends Agent {
	bio?: string;
}

// Profile images are stored as base64 data URLs inside the agent document, and base64
// inflates the payload by roughly a third. 500KB of source image lands around 700KB
// encoded, which keeps the document under Firestore's 1MiB limit.
// Upload limits and formatting come from the shared service so this page and
// AgentManagementPage cannot disagree about what is too large.

interface NotificationSettings {
	newCustomerAssignment: boolean;
	customerRedistribution: boolean;
	newInsightGenerated: boolean;
	highPriorityInsight: boolean;
	customerAssessmentCompleted: boolean;
	leadScoreChanged: boolean;
	customerStatusChanged: boolean;
	insightExpiring: boolean;
	weeklyReports: boolean;
}

export const ProfilePage: React.FC = () => {
	const { currentAgent, currentUser } = useAuth();
	const toast = useRef<Toast>(null);
	// Signed Storage URL for the current photo; the legacy base64 field is used
	// only as a fallback until existing documents are migrated.
	const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
	const [uploadingImage, setUploadingImage] = useState(false);

	const [activeTab, setActiveTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [profileCompletion, setProfileCompletion] = useState(0);
	const [showPasswordDialog, setShowPasswordDialog] = useState(false);
	const [stats, setStats] = useState({
		totalCustomers: 0,
		activeInsights: 0,
		completedTasks: 0,
		conversionRate: 0
	});

	const [formData, setFormData] = useState<ProfileFormData>({
		firstName: '',
		lastName: '',
		displayName: '',
		email: '',
		phone: '',
		companyName: '',
		territory: '',
		licenseNumber: '',
		specialties: [],
		bio: ''
	});

	const [passwordForm, setPasswordForm] = useState<PasswordFormData>({
		currentPassword: '',
		newPassword: '',
		confirmPassword: ''
	});

	const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
		newCustomerAssignment: true,
		customerRedistribution: true,
		newInsightGenerated: true,
		highPriorityInsight: true,
		customerAssessmentCompleted: true,
		leadScoreChanged: true,
		customerStatusChanged: true,
		insightExpiring: true,
		weeklyReports: true
	});

	const [errors, setErrors] = useState<Record<string, string>>({});

	const specialtyOptions = [
		{ label: 'First-Time Buyers', value: 'first_time_buyers' },
		{ label: 'Refinancing', value: 'refinancing' },
		{ label: 'Investment Properties', value: 'investment' },
		{ label: 'Commercial Lending', value: 'commercial' },
		{ label: 'VA Loans', value: 'va_loans' },
		{ label: 'FHA Loans', value: 'fha_loans' },
		{ label: 'Jumbo Loans', value: 'jumbo_loans' }
	];

	const calculateProfileCompletion = useCallback((agent: Agent) => {
		let completed = 0;
		const fields = [
			'firstName', 'lastName', 'email', 'phone',
			'companyName', 'territory', 'licenseNumber',
			'profileImage', 'specialties'
		];

		fields.forEach(field => {
			let value = agent[field as keyof Agent];
			// Support both company and companyName for backward compatibility
			if (field === 'companyName' && !value) {
				value = agent.company;
			}
			if (value && (Array.isArray(value) ? value.length > 0 : true)) {
				completed++;
			}
		});

		setProfileCompletion(Math.round((completed / fields.length) * 100));
	}, []);

	const loadProfileData = useCallback(() => {
		if (!currentAgent) return;

		setFormData({
			firstName: currentAgent.firstName || '',
			lastName: currentAgent.lastName || '',
			displayName: currentAgent.displayName || '',
			email: currentAgent.email,
			phone: currentAgent.phone || '',
			// Support both company and companyName for backward compatibility
			companyName: currentAgent.companyName || currentAgent.company || '',
			territory: currentAgent.territory || '',
			licenseNumber: currentAgent.licenseNumber || '',
			specialties: currentAgent.specialties || [],
			bio: (currentAgent as AgentProfile).bio || '',
			profileImage: currentAgent.profileImage
		});

		calculateProfileCompletion(currentAgent);
	}, [calculateProfileCompletion, currentAgent]);

	const loadStats = useCallback(async () => {
		if (!currentAgent) return;

		try {
			// A count, not a page of records. This used to fetch up to 1000
			// customers purely to read `.length`.
			const [counts, insightStats] = await Promise.all([
				CustomerService.getCustomerCounts(),
				InsightService.getInsightStats(currentAgent.id)
			]);

			setStats({
				totalCustomers: counts.byAgent[currentAgent.id] || 0,
				activeInsights: insightStats.totalInsights - insightStats.newInsights,
				completedTasks: insightStats.totalInsights,
				conversionRate: insightStats.conversionRate
			});
		} catch (error) {
			console.error('Error loading stats:', error);
		}
	}, [currentAgent]);


	const loadNotificationSettings = useCallback(() => {
		if (!currentAgent?.notificationPreferences) return;

		const prefs = currentAgent.notificationPreferences;
		setNotificationSettings({
			newCustomerAssignment: prefs.newCustomerAssignment ?? true,
			customerRedistribution: prefs.customerRedistribution ?? true,
			newInsightGenerated: prefs.newInsightGenerated ?? true,
			highPriorityInsight: prefs.highPriorityInsight ?? true,
			customerAssessmentCompleted: prefs.customerAssessmentCompleted ?? true,
			leadScoreChanged: prefs.leadScoreChanged ?? true,
			customerStatusChanged: prefs.customerStatusChanged ?? true,
			insightExpiring: prefs.insightExpiring ?? true,
			weeklyReports: prefs.weeklyReports ?? true
		});
	}, [currentAgent]);

	useEffect(() => {
		if (currentAgent) {
			loadProfileData();
			loadStats();
			loadNotificationSettings();
		}
	}, [currentAgent, loadProfileData, loadStats, loadNotificationSettings]);

	const validateForm = useCallback((): boolean => {
		const newErrors: Record<string, string> = {};

		if (!formData.firstName.trim()) {
			newErrors.firstName = 'First name is required';
		}

		if (!formData.lastName.trim()) {
			newErrors.lastName = 'Last name is required';
		}

		if (formData.phone && !/^[\d\s\-+()]+$/.test(formData.phone)) {
			newErrors.phone = 'Invalid phone number format';
		}

		if (formData.licenseNumber && !/^[A-Z0-9-]+$/i.test(formData.licenseNumber)) {
			newErrors.licenseNumber = 'Invalid license number format';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [formData.firstName, formData.lastName, formData.phone, formData.licenseNumber]);

	const handleUpdateProfile = useCallback(async () => {
		if (!currentAgent || !validateForm()) return;

		setLoading(true);
		try {
			// Prepare update data - filter out undefined values to avoid Firestore errors
			const updateData: any = {
				firstName: formData.firstName,
				lastName: formData.lastName,
				displayName: formData.displayName || `${formData.firstName} ${formData.lastName}`,
				phone: formData.phone,
				// Set both company and companyName for backward compatibility
				companyName: formData.companyName,
				company: formData.companyName,
				territory: formData.territory,
				licenseNumber: formData.licenseNumber,
				specialties: formData.specialties,
				bio: formData.bio
			};

			// profileImage is not written here. The photo lives in Cloud Storage
			// and is set through uploadAgentProfileImage, which also clears the
			// legacy base64 field.

			await AgentService.updateAgent(currentAgent.id, updateData);

			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: 'Profile updated successfully',
				life: 3000
			});
		} catch (error) {
			console.error('Error updating profile:', error);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to update profile',
				life: 3000
			});
		} finally {
			setLoading(false);
		}
	// formData.profileImage is deliberately absent: the photo goes straight to
	// Cloud Storage and is no longer part of this save.
	}, [currentAgent, formData.bio, formData.companyName, formData.displayName, formData.firstName, formData.lastName, formData.licenseNumber, formData.phone, formData.specialties, formData.territory, validateForm]);

	const handleSaveNotificationPreferences = useCallback(async () => {
		if (!currentAgent) return;

		setLoading(true);
		try {
			await AgentService.updateAgent(currentAgent.id, {
				notificationPreferences: notificationSettings
			});

			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: 'Notification preferences saved',
				life: 3000
			});
		} catch (error) {
			console.error('Error saving notification preferences:', error);
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Failed to save notification preferences',
				life: 3000
			});
		} finally {
			setLoading(false);
		}
	}, [currentAgent, notificationSettings]);

	const handlePasswordChange = useCallback(async () => {
		if (passwordForm.newPassword !== passwordForm.confirmPassword) {
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Passwords do not match',
				life: 3000
			});
			return;
		}

		if (passwordForm.newPassword.length < 8) {
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Password must be at least 8 characters',
				life: 3000
			});
			return;
		}

		// Additional password strength checks
		const hasUpperCase = /[A-Z]/.test(passwordForm.newPassword);
		const hasLowerCase = /[a-z]/.test(passwordForm.newPassword);
		const hasNumbers = /\d/.test(passwordForm.newPassword);
		const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword);

		if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
			toast.current?.show({
				severity: 'error',
				summary: 'Weak Password',
				detail: 'Password must contain uppercase, lowercase, numbers, and special characters',
				life: 5000
			});
			return;
		}
		if (!passwordForm.currentPassword) {
			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: 'Please enter your current password',
				life: 3000
			});
			return;
		}

		setLoading(true);
		try {
			const user = auth.currentUser;

			if (!user || !user.email) {
				throw new Error('No authenticated user found');
			}

			// Re-authenticate the user with their current password
			const credential = EmailAuthProvider.credential(
				user.email,
				passwordForm.currentPassword
			);

			await reauthenticateWithCredential(user, credential);

			// Update the password
			await updatePassword(user, passwordForm.newPassword);

			toast.current?.show({
				severity: 'success',
				summary: 'Success',
				detail: 'Password changed successfully',
				life: 3000
			});

			setShowPasswordDialog(false);
			setPasswordForm({
				currentPassword: '',
				newPassword: '',
				confirmPassword: ''
			});
		} catch (error) {
			let errorMessage = 'Failed to change password';

			// Type guard for Firebase Auth errors
			const isAuthError = (err: unknown): err is { code: string } => {
				return typeof err === 'object' && err !== null && 'code' in err;
			};

			if (isAuthError(error)) {
				if (error.code === 'auth/wrong-password') {
					errorMessage = 'Current password is incorrect';
				} else if (error.code === 'auth/weak-password') {
					errorMessage = 'New password is too weak. Please choose a stronger password';
				} else if (error.code === 'auth/requires-recent-login') {
					errorMessage = 'For security reasons, please log out and log in again before changing your password';
				} else if (error.code === 'auth/network-request-failed') {
					errorMessage = 'Network error. Please check your connection and try again';
				}
			}

			toast.current?.show({
				severity: 'error',
				summary: 'Error',
				detail: errorMessage,
				life: 5000
			});
		} finally {
			setLoading(false);
		}
	}, [passwordForm]);

	const handleImageUpload = async (event: { files: File[] }) => {
		const file = event.files[0];
		if (!file) return;

		// Uploaded to Cloud Storage rather than embedded in the agent document.
		// Base64 in Firestore pushed a 2 MB photo past the 1 MiB document limit
		// and rode along on every read of the record.
		const problem = ProfileImageService.validate(file);
		if (problem) {
			toast.current?.show({
				severity: 'error',
				summary: 'Cannot use that image',
				detail: problem,
				life: 6000
			});
			return;
		}

		setUploadingImage(true);
		try {
			const url = await ProfileImageService.upload(file);
			setAvatarUrl(url);
			setFormData(prev => ({ ...prev, profileImage: undefined }));
			toast.current?.show({
				severity: 'success',
				summary: 'Photo updated',
				life: 3000
			});
		} catch (error: any) {
			toast.current?.show({
				severity: 'error',
				summary: 'Upload failed',
				detail: error?.message || 'Could not upload that image.',
				life: 6000
			});
		} finally {
			setUploadingImage(false);
		}
	};



	return (
		<div className="profile-page">
			<Toast ref={toast} />

			<div className="page-header">
				<h1>My Profile</h1>
				<p>Manage your profile and account settings</p>
			</div>

			{/* Profile Overview */}
			<Card className="profile-overview-card">
				<div className="profile-header">
					<div className="profile-info">
						<Avatar
							image={avatarUrl || formData.profileImage}
							label={!(avatarUrl || formData.profileImage) ? getInitials(formData.firstName, formData.lastName) : undefined}
							size="xlarge"
							shape="circle"
							className="profile-avatar"
						/>
						<div className="profile-details">
							<h2>{currentAgent?.displayName}</h2>
							<p>{currentAgent?.email}</p>
							<div className="profile-badges">
								<AgentRoleIndicator role={currentAgent?.role || 'agent'} />
								{currentAgent?.territory && <Chip label={currentAgent.territory} />}
							</div>
						</div>
					</div>
					<div className="profile-stats">
						<div className="stat">
							<div className="stat-value">{stats.totalCustomers}</div>
							<div className="stat-label">Customers</div>
						</div>
						<div className="stat">
							<div className="stat-value">{stats.activeInsights}</div>
							<div className="stat-label">Active Insights</div>
						</div>
						<div className="stat">
							<div className="stat-value">{stats.conversionRate.toFixed(1)}%</div>
							<div className="stat-label">Conversion Rate</div>
						</div>
					</div>
				</div>

				<Divider />

				<div className="profile-completion">
					<div className="completion-header">
						<span>Profile Completion</span>
						<span>{profileCompletion}%</span>
					</div>
					<ProgressBar value={profileCompletion} showValue={false} />
					{profileCompletion < 100 && (
						<p className="completion-hint">Complete your profile to improve customer trust</p>
					)}
				</div>
			</Card>

			<TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
				<TabPanel header="Personal Information">
					<Card className="settings-card">
						<div className="form-grid">
							<div className="field">
								<label htmlFor="firstName">First Name *</label>
								<InputText
									id="firstName"
									value={formData.firstName}
									onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
									className={errors.firstName ? 'p-invalid' : ''}
								/>
								{errors.firstName && <small className="p-error">{errors.firstName}</small>}
							</div>

							<div className="field">
								<label htmlFor="lastName">Last Name *</label>
								<InputText
									id="lastName"
									value={formData.lastName}
									onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
									className={errors.lastName ? 'p-invalid' : ''}
								/>
								{errors.lastName && <small className="p-error">{errors.lastName}</small>}
							</div>

							<div className="field">
								<label htmlFor="displayName">Display Name</label>
								<InputText
									id="displayName"
									value={formData.displayName}
									onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
									placeholder="How you want to be displayed"
								/>
							</div>

							<div className="field">
								<label htmlFor="email">Email</label>
								<InputText
									id="email"
									value={formData.email}
									disabled
								/>
								<small>Email cannot be changed</small>
							</div>

							<div className="field">
								<label htmlFor="phone">Phone</label>
								<InputMask
									id="phone"
									mask="(999) 999-9999"
									value={formData.phone}
									onChange={(e) => setFormData({ ...formData, phone: e.value || '' })}
									placeholder="(555) 123-4567"
									className={errors.phone ? 'p-invalid' : ''}
								/>
								{errors.phone && <small className="p-error">{errors.phone}</small>}
							</div>

							<div className="field">
								<label htmlFor="territory">Territory</label>
								<InputText
									id="territory"
									value={formData.territory}
									onChange={(e) => setFormData({ ...formData, territory: e.target.value })}
									placeholder="e.g., North Region"
								/>
							</div>

							<div className="field">
								<label htmlFor="licenseNumber">License Number</label>
								<InputText
									id="licenseNumber"
									value={formData.licenseNumber}
									onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
									placeholder="e.g., NMLS-123456"
									className={errors.licenseNumber ? 'p-invalid' : ''}
								/>
								{errors.licenseNumber && <small className="p-error">{errors.licenseNumber}</small>}
							</div>

							<div className="field">
								<label htmlFor="specialties">Specialties</label>
								<MultiSelect
									id="specialties"
									value={formData.specialties}
									options={specialtyOptions}
									onChange={(e) => setFormData({ ...formData, specialties: e.value })}
									placeholder="Select Specialties"
									display="comma"
									maxSelectedLabels={3}
								/>
							</div>

							<div className="field full-width">
								<label htmlFor="bio">Bio</label>
								<InputTextarea
									id="bio"
									value={formData.bio}
									onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
									rows={4}
									placeholder="Tell us about yourself..."
								/>
							</div>

							<div className="field">
								<label>Profile Image</label>
								<div className="image-upload-container">
									<Avatar
										image={avatarUrl || formData.profileImage}
										label={!(avatarUrl || formData.profileImage) ? getInitials(formData.firstName, formData.lastName) : undefined}
										size="xlarge"
										shape="circle"
									/>
									<FileUpload
										mode="basic"
										accept={ALLOWED_PROFILE_IMAGE_TYPES.join(',')}
										maxFileSize={MAX_PROFILE_IMAGE_BYTES}
										onSelect={handleImageUpload}
										disabled={uploadingImage}
										chooseLabel="Upload Photo"
										auto={false}
									/>
									<small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280' }}>
										Accepted formats: JPG, PNG, GIF, WebP. Max size: {formatFileSize(MAX_PROFILE_IMAGE_BYTES)}
									</small>
								</div>
							</div>
						</div>

						<div className="modern-form-actions">
							<ModernButton
								variant="primary"
								icon="pi pi-save"
								onClick={handleUpdateProfile}
								loading={loading}
							>
								Save Changes
							</ModernButton>
						</div>
					</Card>
				</TabPanel>

				<TabPanel header="Security">
					<Card className="settings-card">
						<h3>Security Settings</h3>

						<div className="security-section">
							<div className="security-item">
								<div className="security-info">
									<h4>Password</h4>
									<p>Last changed {currentUser?.metadata.creationTime ?
										new Date(currentUser.metadata.creationTime).toLocaleDateString() :
										'Never'}</p>
								</div>
								<ModernButton
									variant="outline"
									icon="pi pi-lock"
									onClick={() => setShowPasswordDialog(true)}
								>
									Change Password
								</ModernButton>
							</div>
						</div>
					</Card>
				</TabPanel>

				<TabPanel header="Notifications">
					<Card className="settings-card">
						<h3>Notification Preferences</h3>
						<p className="settings-description">
							Choose which events you want to be notified about. All notifications appear in your notification center.
						</p>

						<div className="notification-settings">
							<h4>Customer Events</h4>

							<div className="notification-item">
								<div className="notification-info">
									<h4>New Customer Assignment</h4>
									<p>Get notified when a new customer is assigned to you</p>
								</div>
								<InputSwitch
									checked={notificationSettings.newCustomerAssignment}
									onChange={(e) => setNotificationSettings({
										...notificationSettings,
										newCustomerAssignment: e.value
									})}
								/>
							</div>

							<div className="notification-item">
								<div className="notification-info">
									<h4>Customer Redistribution</h4>
									<p>Alerts when customers are reassigned from or to you</p>
								</div>
								<InputSwitch
									checked={notificationSettings.customerRedistribution}
									onChange={(e) => setNotificationSettings({
										...notificationSettings,
										customerRedistribution: e.value
									})}
								/>
							</div>

							<div className="notification-item">
								<div className="notification-info">
									<h4>Customer Assessment Completed</h4>
									<p>Notifies when a customer completes an assessment</p>
								</div>
								<InputSwitch
									checked={notificationSettings.customerAssessmentCompleted}
									onChange={(e) => setNotificationSettings({
										...notificationSettings,
										customerAssessmentCompleted: e.value
									})}
								/>
							</div>

							<div className="notification-item">
								<div className="notification-info">
									<h4>Customer Status Changed</h4>
									<p>Alerts when customer status changes (prospect, active, converted, etc.)</p>
								</div>
								<InputSwitch
									checked={notificationSettings.customerStatusChanged}
									onChange={(e) => setNotificationSettings({
										...notificationSettings,
										customerStatusChanged: e.value
									})}
								/>
							</div>

							<div className="notification-item">
								<div className="notification-info">
									<h4>Lead Score Changed</h4>
									<p>Get notified when a customer's lead score changes significantly</p>
								</div>
								<InputSwitch
									checked={notificationSettings.leadScoreChanged}
									onChange={(e) => setNotificationSettings({
										...notificationSettings,
										leadScoreChanged: e.value
									})}
								/>
							</div>

							<Divider />

							<h4>Insight Notifications</h4>

							<div className="notification-item">
								<div className="notification-info">
									<h4>New Insights Generated</h4>
									<p>Get notified when new insights are created for your customers</p>
								</div>
								<InputSwitch
									checked={notificationSettings.newInsightGenerated}
									onChange={(e) => setNotificationSettings({
										...notificationSettings,
										newInsightGenerated: e.value
									})}
								/>
							</div>

							<div className="notification-item">
								<div className="notification-info">
									<h4>High Priority Insights</h4>
									<p>Special alerts for urgent and high-priority insights</p>
								</div>
								<InputSwitch
									checked={notificationSettings.highPriorityInsight}
									onChange={(e) => setNotificationSettings({
										...notificationSettings,
										highPriorityInsight: e.value
									})}
								/>
							</div>

							<div className="notification-item">
								<div className="notification-info">
									<h4>Insight Expiring Soon</h4>
									<p>Reminder when insights are about to expire</p>
								</div>
								<InputSwitch
									checked={notificationSettings.insightExpiring}
									onChange={(e) => setNotificationSettings({
										...notificationSettings,
										insightExpiring: e.value
									})}
								/>
							</div>

							<Divider />

							<h4>Reports & Analytics</h4>

							<div className="notification-item">
								<div className="notification-info">
									<h4>Weekly Performance Reports</h4>
									<p>Receive weekly summaries of your performance metrics</p>
								</div>
								<InputSwitch
									checked={notificationSettings.weeklyReports}
									onChange={(e) => setNotificationSettings({
										...notificationSettings,
										weeklyReports: e.value
									})}
								/>
							</div>
						</div>

						<div className="modern-form-actions">
							<ModernButton
								variant="primary"
								icon="pi pi-save"
								onClick={handleSaveNotificationPreferences}
								loading={loading}
							>
								Save Preferences
							</ModernButton>
						</div>
					</Card>
				</TabPanel>
			</TabView>

			{/* Password Change Dialog */}
			<Dialog
				visible={showPasswordDialog}
				onHide={() => setShowPasswordDialog(false)}
				header="Change Password"
				style={{ width: '400px' }}
				footer={
					<div className="modern-form-actions">
						<ModernButton variant="ghost" onClick={() => setShowPasswordDialog(false)}>
							Cancel
						</ModernButton>
						<ModernButton variant="primary" onClick={handlePasswordChange} loading={loading}>
							Change Password
						</ModernButton>
					</div>
				}
			>
				<div className="password-form">
					<div className="field">
						<label htmlFor="currentPassword">Current Password</label>
						<Password
							id="currentPassword"
							value={passwordForm.currentPassword}
							onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
							feedback={false}
							toggleMask
						/>
					</div>

					<div className="field">
						<label htmlFor="newPassword">New Password</label>
						<Password
							id="newPassword"
							value={passwordForm.newPassword}
							onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
							toggleMask
						/>
					</div>

					<div className="field">
						<label htmlFor="confirmPassword">Confirm New Password</label>
						<Password
							id="confirmPassword"
							value={passwordForm.confirmPassword}
							onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
							feedback={false}
							toggleMask
						/>
					</div>
				</div>
			</Dialog>
		</div>
	);
};

export default ProfilePage;
